// GAVION custom pipeline (spec docs/superpowers/specs/2026-10-16-deal-pipeline-custom-stages-design.md §1). The one TS
// stage helper: the board, the deal pages, the report twins, the import, the automation editor and Paramètres →
// Pipeline read the configured stages through these functions. SQL twins (supabase/schemas/61_gavion_deal_stages.sql):
// DEFAULT_DEAL_STAGES / STAGE_PALETTE = private.default_deal_stages / private.deal_stage_palette (pinned by
// scripts/gavion/opsParity.test.ts), nextStageColor = private.next_stage_color, normalizeStages =
// private.deal_stages_from_legacy, stageOrder = public.gavion_stage_order, stageProbability =
// public.gavion_stage_probability, isAfterQualifie = public.gavion_stage_after_qualifie, validateStages = the
// refusal codes of public.set_deal_stages. Pure: no React, importable from Node (supabase/functions tests).

export type StageDef = {
  value: string;
  label: string;
  color: string;
  probability: number;
};
export type StageKind = "open" | "won" | "lost";

export const WON_STAGE = "gagne";
export const LOST_STAGE = "perdu";
/** Never deleted; they can be renamed, recoloured and (open ones) reordered. */
export const SYSTEM_STAGES = [
  "nouveau_lead",
  "qualifie",
  "gagne",
  "perdu",
] as const;
export const STAGE_LABEL_MAX = 60;
export const STAGE_VALUE_RE = /^[a-z0-9_]{1,60}$/;
export const STAGE_COLOR_RE = /^#[0-9a-fA-F]{6}$/;
/** A stage the configuration does not know (a deal written with a deleted stage). */
export const UNKNOWN_STAGE_COLOR = "#94a3b8";
export const DARK_TEXT = "#111827";
export const LIGHT_TEXT = "#ffffff";

export const DEFAULT_DEAL_STAGES: readonly StageDef[] = Object.freeze([
  {
    value: "nouveau_lead",
    label: "Nouveau lead",
    color: "#64748b",
    probability: 0.05,
  },
  { value: "qualifie", label: "Qualifié", color: "#2563eb", probability: 0.2 },
  {
    value: "proposition_envoyee",
    label: "Proposition envoyée",
    color: "#7c3aed",
    probability: 0.5,
  },
  {
    value: "negociation",
    label: "Négociation",
    color: "#d97706",
    probability: 0.7,
  },
  { value: "gagne", label: "Gagné", color: "#16a34a", probability: 1 },
  { value: "perdu", label: "Perdu", color: "#dc2626", probability: 0 },
]);

export const STAGE_PALETTE: readonly string[] = Object.freeze([
  "#64748b",
  "#2563eb",
  "#7c3aed",
  "#d97706",
  "#16a34a",
  "#dc2626",
  "#0891b2",
  "#db2777",
  "#65a30d",
  "#ea580c",
  "#4f46e5",
  "#0d9488",
]);

export const stageKind = (value: string): StageKind =>
  value === WON_STAGE ? "won" : value === LOST_STAGE ? "lost" : "open";
/** Kinds are fixed by value: every stage but gagne and perdu is open, an unknown one too. */
export const isOpenStage = (value: string): boolean =>
  stageKind(value) === "open";
export const isSystemStage = (value: string): boolean =>
  (SYSTEM_STAGES as readonly string[]).includes(value);

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));
const findStage = (stages: readonly StageDef[], value: string) =>
  stages.find((s) => s.value === value);

/** Open stages in their configured order, then Gagné, then Perdu (the board's and the editor's order). */
export const orderedStages = (stages: readonly StageDef[]): StageDef[] => [
  ...stages.filter((s) => isOpenStage(s.value)),
  ...stages.filter((s) => s.value === WON_STAGE),
  ...stages.filter((s) => s.value === LOST_STAGE),
];

/** The first palette colour no stage uses; when all are used, the palette cycles. */
export const nextStageColor = (
  stages: readonly { color: string }[],
): string => {
  const used = new Set(stages.map((s) => s.color.toLowerCase()));
  return (
    STAGE_PALETTE.find((c) => !used.has(c)) ??
    STAGE_PALETTE[stages.length % STAGE_PALETTE.length]
  );
};

const num01 = (x: unknown): number | null =>
  typeof x === "number" && Number.isFinite(x) ? clamp01(x) : null;

/**
 * A stored config.dealStages (any shape) as a complete list. Entries without a value are dropped, a repeated value keeps
 * its first entry; nouveau_lead / qualifie missing → added first; gagne and perdu always last; `used` values the list
 * lacks → open, after the configured open ones (sorted). Label: trimmed, else the default, else the value (60 max);
 * colour: a valid #rrggbb (lower case), else the default, else the next palette colour; probability: gagne 1, perdu 0,
 * else the legacy config.gavion.stage_probabilities value, else the entry's, else the default, else 0 (clamped).
 */
export const normalizeStages = (
  raw: unknown,
  legacyProbabilities?: Record<string, unknown> | null,
  used: readonly string[] = [],
): StageDef[] => {
  const src: readonly unknown[] =
    Array.isArray(raw) && raw.length > 0 ? raw : DEFAULT_DEAL_STAGES;
  const entries = new Map<string, Record<string, unknown>>();
  for (const e of src) {
    if (!e || typeof e !== "object" || Array.isArray(e)) continue;
    const v = (e as Record<string, unknown>).value;
    if (typeof v === "string" && v.trim() !== "" && !entries.has(v))
      entries.set(v, e as Record<string, unknown>);
  }
  const extra = [...new Set(used)]
    .filter(
      (v) =>
        v.trim() !== "" &&
        isOpenStage(v) &&
        !entries.has(v) &&
        v !== "nouveau_lead" &&
        v !== "qualifie",
    )
    .sort();
  const values = [
    ...["nouveau_lead", "qualifie"].filter((v) => !entries.has(v)),
    ...[...entries.keys()].filter(isOpenStage),
    ...extra,
    WON_STAGE,
    LOST_STAGE,
  ];
  const probs = legacyProbabilities ?? {};
  const firstPass = values.map((value) => {
    const e = entries.get(value);
    const d = findStage(DEFAULT_DEAL_STAGES, value);
    const rawLabel = typeof e?.label === "string" ? e.label.trim() : "";
    const label = (rawLabel || d?.label || value).slice(0, STAGE_LABEL_MAX);
    const color =
      typeof e?.color === "string" && STAGE_COLOR_RE.test(e.color)
        ? e.color.toLowerCase()
        : (d?.color ?? null);
    const probability =
      value === WON_STAGE
        ? 1
        : value === LOST_STAGE
          ? 0
          : (num01(probs[value]) ??
            num01(e?.probability) ??
            d?.probability ??
            0);
    return { value, label, color, probability };
  });
  const colored: { color: string }[] = firstPass.flatMap((s) =>
    s.color === null ? [] : [{ color: s.color }],
  );
  return firstPass.map((s) => {
    if (s.color !== null) return { ...s, color: s.color };
    const color = nextStageColor(colored);
    colored.push({ color });
    return { ...s, color };
  });
};

/**
 * The legacy config.gavion.stage_probabilities to overlay at run time: only while the configuration has no stored
 * dealStages list (before the §5 conversion, the same input as private.deal_stages_from_legacy). Once dealStages is
 * stored, each stage's own probability wins, as in SQL (gavion_stage_probability ignores the legacy key and the
 * configuration guard drops it).
 */
export const legacyProbabilities = (
  config: unknown,
): Record<string, unknown> | null => {
  if (!config || typeof config !== "object") return null;
  const c = config as {
    dealStages?: unknown;
    gavion?: { stage_probabilities?: unknown };
  };
  if (Array.isArray(c.dealStages) && c.dealStages.length > 0) return null;
  const p = c.gavion?.stage_probabilities;
  return p && typeof p === "object" && !Array.isArray(p)
    ? (p as Record<string, unknown>)
    : null;
};

/** Open 1…n in the configured order, gagne n + 1, perdu / null → null, a value the config lacks → n. */
export const stageOrder = (
  stages: readonly StageDef[],
  value: string | null | undefined,
): number | null => {
  if (value == null || value === LOST_STAGE) return null;
  const open = stages.filter((s) => isOpenStage(s.value));
  if (value === WON_STAGE) return open.length + 1;
  const i = open.findIndex((s) => s.value === value);
  return i >= 0 ? i + 1 : open.length;
};

export const stageProbability = (
  stages: readonly StageDef[],
  value: string,
): number =>
  value === WON_STAGE
    ? 1
    : value === LOST_STAGE
      ? 0
      : (num01(findStage(stages, value)?.probability) ?? 0);

export const stageProbabilities = (
  stages: readonly StageDef[],
): Record<string, number> =>
  Object.fromEntries(
    stages.map((s) => [s.value, stageProbability(stages, s.value)]),
  );

/** « A stage after Qualifié » (gagne included, perdu never). */
export const isAfterQualifie = (
  stages: readonly StageDef[],
  value: string,
): boolean =>
  (stageOrder(stages, value) ?? 0) > (stageOrder(stages, "qualifie") ?? 0);

export const stageColor = (
  stages: readonly StageDef[],
  value: string,
): string => findStage(stages, value)?.color ?? UNKNOWN_STAGE_COLOR;

const channel = (c: number) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};
const luminance = (hex: string) => {
  const n = parseInt(hex.slice(1), 16);
  return (
    0.2126 * channel((n >> 16) & 255) +
    0.7152 * channel((n >> 8) & 255) +
    0.0722 * channel(n & 255)
  );
};
const contrast = (a: number, b: number) =>
  (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
/** Badge text: white or dark (#111827), whichever has the higher WCAG contrast on the colour. */
export const readableTextColor = (hex: string): string => {
  if (!STAGE_COLOR_RE.test(hex)) return DARK_TEXT;
  const l = luminance(hex);
  return contrast(l, 1) >= contrast(l, luminance(DARK_TEXT))
    ? LIGHT_TEXT
    : DARK_TEXT;
};

/**
 * The slug of a label shared by stages and lead statuses: without accents, lower case, `_` between words (50 max),
 * `fallback` when nothing is left, never one of `taken` or `reserved` (a clash gets `_2`, `_3`, …).
 */
export const slugFromLabel = (
  label: string,
  taken: readonly string[],
  reserved: readonly string[],
  fallback: string,
): string => {
  const base =
    label
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 50)
      .replace(/_+$/, "") || fallback;
  const blocked: readonly string[] = [...taken, ...reserved];
  let slug = base;
  for (let n = 2; blocked.includes(slug); n++) slug = `${base}_${n}`;
  return slug;
};

/** A new stage's value: its label without accents, lower case, `_` between words (50 max), unique among `taken` and the system stages. */
export const stageSlug = (label: string, taken: readonly string[]): string =>
  slugFromLabel(label, taken, SYSTEM_STAGES, "etape");

export type StageIssueCode =
  | "invalid_stage_value"
  | "duplicate_stage"
  | "invalid_stage_label"
  | "invalid_stage_color"
  | "invalid_stage_probability"
  | "missing_system_stage";
export type StageIssue = { code: StageIssueCode; value: string };

/** The client-side twin of public.set_deal_stages's checks (same codes, same order). */
export const validateStages = (
  stages: readonly StageDef[],
): StageIssue | null => {
  const seen = new Set<string>();
  for (const s of stages) {
    if (!STAGE_VALUE_RE.test(s.value))
      return { code: "invalid_stage_value", value: s.value };
    if (seen.has(s.value)) return { code: "duplicate_stage", value: s.value };
    seen.add(s.value);
    const label = s.label.trim();
    if (!label || label.length > STAGE_LABEL_MAX)
      return { code: "invalid_stage_label", value: s.value };
    if (!STAGE_COLOR_RE.test(s.color))
      return { code: "invalid_stage_color", value: s.value };
    if (
      isOpenStage(s.value) &&
      !(
        Number.isFinite(s.probability) &&
        s.probability >= 0 &&
        s.probability <= 1
      )
    )
      return { code: "invalid_stage_probability", value: s.value };
  }
  const missing = SYSTEM_STAGES.filter((v) => !seen.has(v));
  return missing.length
    ? { code: "missing_system_stage", value: missing.join(", ") }
    : null;
};

/** A built-in stage still carries its default (French) label: the screens show its translation. */
export const hasDefaultLabel = (stage: StageDef): boolean =>
  findStage(DEFAULT_DEAL_STAGES, stage.value)?.label === stage.label;

/** The label shown for a stage value (spec §1): the owner's label once renamed, in both languages. */
export const stageLabelFor = (
  stages: readonly StageDef[],
  value: string,
  translate: (key: string, options: { _: string }) => string,
): string => {
  const stage = findStage(stages, value);
  if (!stage) return translate(`gavion.deals.stages.${value}`, { _: value });
  return hasDefaultLabel(stage)
    ? translate(`gavion.deals.stages.${value}`, { _: stage.label })
    : stage.label;
};
